import {
  convertDistance,
  convertTemperature,
} from 'Components/NormalizationSettings/components/NormalizationSettingsForm/utils';
import {
  BallType,
  DEFAULT_NORMALIZATION_VALUES,
  DistanceUnit,
  NormalizationSettingsType,
  TemperatureUnit,
} from 'Components/NormalizationSettings/configs';
import { ReportKind, UrlStateParameters } from 'Data';
import {
  CompareReportStore,
  DrShotAnalysisReportStore,
  GeneralReportStore,
  TestReportStore,
} from 'Reports';

export const parseNumber = (value: string | null, defaultValue: number | null = null) => {
  if (value === null) {
    return defaultValue;
  }
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? defaultValue : parsedValue;
};

export const rawSearchParamToOption = <T extends string>(
  rawSearchParam: string | null,
  options: T[]
) => {
  const option = options?.find(option => option === rawSearchParam);
  return option ?? null;
};

export const getNormalizationWithDefaultValuesIfEmpty = (
  normalizationSettings: Partial<NormalizationSettingsType>
) => {
  if (!normalizationSettings) {
    return DEFAULT_NORMALIZATION_VALUES;
  }
  const settingsWithValue = Object.entries(normalizationSettings).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined) {
      return {
        ...acc,
        [key]: value,
      };
    }
    return acc;
  }, {});
  return {
    ...DEFAULT_NORMALIZATION_VALUES,
    ...settingsWithValue,
  };
};

export const getReportInitialUrlParams = () => {
  const searchParameters = new URLSearchParams(window.location.search);
  const reportId =
    searchParameters.get(UrlStateParameters.reportId) ?? searchParameters.get('ReportId');
  const normalization: NormalizationSettingsType = {
    ballType: searchParameters.get(UrlStateParameters.ballType) as BallType,
    altitude: parseNumber(searchParameters.get(UrlStateParameters.altitude)),
    temperature: parseNumber(searchParameters.get(UrlStateParameters.temperature)),
    altitudeUnit: rawSearchParamToOption(searchParameters.get(UrlStateParameters.altitudeUnit), [
      DistanceUnit.Meters,
      DistanceUnit.Feet,
    ]),
    temperatureUnit: rawSearchParamToOption(
      searchParameters.get(UrlStateParameters.temperatureUnit),
      [TemperatureUnit.Celsius, TemperatureUnit.Fahrenheit]
    ),
  };
  return {
    reportId,
    normalization: getNormalizationWithDefaultValuesIfEmpty(normalization),
  };
};

export const convertNormalizationToStandardUnits = (normalization: NormalizationSettingsType) => {
  const { altitude, altitudeUnit, temperature, temperatureUnit } = normalization;
  const altitudeInMeters = convertDistance(altitude, {
    sourceUnit: altitudeUnit,
    targetUnit: DistanceUnit.Meters,
  });
  const temperatureInCelsius = convertTemperature(temperature, {
    sourceUnit: temperatureUnit,
    targetUnit: TemperatureUnit.Celsius,
  });
  return {
    ...normalization,
    altitude: altitudeInMeters,
    temperature: temperatureInCelsius,
    altitudeUnit: DistanceUnit.Meters,
    temperatureUnit: TemperatureUnit.Celsius,
  };
};

export const getReportStoreByKind = (reportKind: ReportKind) => {
  switch (reportKind) {
    case ReportKind.multiGroupReport:
    case ReportKind.puttingMultiGroupReport:
    case ReportKind.singleGroupReport:
    case ReportKind.puttingSingleGroupReport:
    case ReportKind.singleShotReport:
    case ReportKind.puttingSingleShotReport:
      return new GeneralReportStore();
    case ReportKind.compareReport:
    case ReportKind.puttingCompareReport:
      return new CompareReportStore();
    case ReportKind.combineTestReport:
    case ReportKind.testCenterReport:
      return new TestReportStore();
    case ReportKind.drShotAnalysis:
      return new DrShotAnalysisReportStore();
    default:
      return new GeneralReportStore();
  }
};
